import { AccountProfilePageModel } from './model';
import { RouterState, RedirectFunction } from 'react-router';
import { action, runInAction } from 'mobx';
import { APIController } from '../../api/controller';
import { UserModel } from "../../user/model";
import { UIController } from '../../ui/controller';
import { ContentModel } from '../../content/model';
import { EnvModel } from '../../env/model';
import { AuthController } from '../../auth/controller';
import {createDetailsForm, createMarketingPreferencesForm, resetFormErrors} from '../../../lib/form';
import {HttpError} from '../../../types';
import {FormDetailsFields} from '../../../components/form-details';
import {FieldValues} from '../../../types/form';


export class AccountProfilePageController {
  constructor(
    private user: UserModel,
    private model: AccountProfilePageModel,
    private api: APIController,
    private ui: UIController,
    private content: ContentModel,
    private env: EnvModel,
    private auth: AuthController,
  ) {}

  @action
  onEnter = async(nextState: RouterState) => {
    runInAction(() => {
      this.model.loading = true;
    });

    await this.ui.setSEO(nextState.location.pathname);

    this.ui.setBreadcrumbs([
      {
        path: '/account',
        label: 'Account'
      },
      {
        path: '/account/profile',
        label: 'Profile'
      }
    ]);

    runInAction(() => {
      this.model.loading = false;
    });

    return;
  };

  @action
  editDetails = async () => {
    await this.env.ready;

    const { user } = this.user;

    const values: FieldValues<FormDetailsFields> = {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      country: user.country,
      language: user.language,
      preferred_genre: user.preferred_genre,
      telephone: user.telephone,
      is_business: user.is_business ? 1 : 0,
      found_us: user.found_us || ''
    };

    runInAction(() => {
      this.model.modalDetails.form = createDetailsForm(values, this.content.genres);
      this.model.modalDetails.show = true;
    });
  };

  @action
  changeDetailsField = (field: string, value: string) => {
    this.model.modalDetails.form.fields[field].value = value;
  };

  @action
  closeDetailsModal = () => {
    this.model.modalDetails.show = false;
  };

  @action
  submitDetailsModal = async () => {
    resetFormErrors(this.model.modalDetails.form);
    const { fields } = this.model.modalDetails.form;

    if (!fields.email.value || fields.email.value.includes('@pages.plusgoogle.com')) {
      return fields.email.error = 'Please enter your email address';
    }

    if (!fields.first_name.value) {
      return fields.first_name.error = 'Please enter your first name';
    }

    if (!fields.last_name.value) {
      return fields.last_name.error = 'Please enter your last name';
    }

    if (!fields.country.value) {
      return fields.country.error = 'Please select a country';
    }

    const values: any = Object.keys(fields).reduce((vals, key) => ({ ...vals, [key]: fields[key].value }), {});

    values.is_business = !!parseInt(values.is_business);

    try {
      this.model.modalDetails.form.saving = true;
      await this.api.user.setContactDetails(values);
      await this.auth.getSession();
      this.closeDetailsModal();
    } catch (e) {
      try {
        this.model.modalDetails.form.error = (e as HttpError).body.error;
      } catch (e) {
        console.log(e);
        this.model.modalDetails.form.error = 'Oops! Something went wrong, please try again.'
      }
    } finally {
      this.model.modalDetails.form.saving = false;
    }
  };


  @action
  editMarketing = async () => {
    await this.env.ready;

    const { user } = this.user;

    runInAction(() => {
      this.model.modalMarketing.form = createMarketingPreferencesForm(user.marketing_allowed);
      this.model.modalMarketing.show = true;
    });
  };

  @action
  changeMarketingField = (field: string, value: string) => {
    this.model.modalMarketing.form.fields[field].value = value;
  };

  @action
  closeMarketingModal = () => {
    this.model.modalMarketing.show = false;
  };

  @action
  submitMarketingModal = async () => {
    resetFormErrors(this.model.modalMarketing.form);

    try {
      this.model.modalMarketing.form.saving = true;
      const marketing_allowed = this.model.modalMarketing.form.fields.marketing_allowed.value;
      const promises: Array<Promise<any>> = [this.api.user.setMarketingPreferences(marketing_allowed)];

      if (!marketing_allowed) {
        promises.push(this.api.user.clearPreferences(this.user.user.identity))
      }

      await Promise.all(promises);
      await this.auth.getSession();
      this.closeMarketingModal();
    } catch (e) {
      try {
        this.model.modalMarketing.form.error = (e as HttpError).body.error;
      } catch (e) {
        console.log(e);
        this.model.modalMarketing.form.error = 'Oops! Something went wrong, please try again.'
      }
    } finally {
      this.model.modalMarketing.form.saving = false;
    }
  };
}